import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useUsageLimits } from '../../hooks/useUsageLimits';
import { getUsageHeadline, getUsageSubtext, pickPrimaryUsageLines } from '../../utils/usageLimits';
import { trackProductEvent } from '../../utils/productEvents';

const sectionCard =
  'rounded-2xl border border-[#e8e2d8] bg-[#fffcf7]/90 shadow-sm';

/**
 * Gentle, non-blocking usage visibility for account or archive settings.
 */
export function UsageLimitsPanel({ treeId = null, className = '', 'data-testid': testId = 'usage-limits-panel' }) {
  const { snapshot, loading, error } = useUsageLimits({ treeId, enabled: true });
  const telemetrySentRef = useRef(null);

  useEffect(() => {
    if (!snapshot || loading) return;

    const contextKey = `${snapshot.scope || 'account'}:${treeId || 'account'}:${snapshot.plan_type || 'free'}`;
    if (telemetrySentRef.current === contextKey) return;
    telemetrySentRef.current = contextKey;

    const flags = snapshot.soft_flags || {};
    const scopeMeta = { scope: snapshot.scope || 'account', tree_id: treeId || undefined };

    if (flags.people_near_limit || flags.memories_near_limit || flags.photos_near_limit) {
      trackProductEvent('limit_near', { pillar: 'billing', metadata: scopeMeta });
    }
    if (flags.people_over_limit || flags.memories_over_limit || flags.photos_over_limit) {
      trackProductEvent('limit_over', { pillar: 'billing', metadata: scopeMeta });
    }
    trackProductEvent('upgrade_prompt_seen', {
      pillar: 'billing',
      metadata: { ...scopeMeta, passive: true },
    });
  }, [snapshot, loading, treeId]);

  if (loading) {
    return (
      <Card className={`${sectionCard} ${className}`} data-testid={testId}>
        <CardHeader className="pb-2">
          <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Archive usage</CardTitle>
          <CardDescription className="text-[#5c534a]">Loading your starter usage…</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error || !snapshot?.usage) {
    return null;
  }

  const headline = getUsageHeadline(snapshot);
  const subtext = getUsageSubtext(snapshot);
  const lines = pickPrimaryUsageLines(snapshot);

  return (
    <Card className={`${sectionCard} ${className}`} data-testid={testId}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#c9a961]/15 text-[#8b7355]">
            <BarChart3 className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Archive usage</CardTitle>
            <CardDescription className="text-[#5c534a]">{headline}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {subtext ? (
          <p className="text-sm leading-relaxed text-[#5c534a]">{subtext}</p>
        ) : null}
        <ul className="space-y-1.5 text-sm text-[#4a433c]">
          {lines.map((line) => (
            <li key={line.key} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a961]" aria-hidden />
              <span>{line.text}</span>
            </li>
          ))}
        </ul>
        {snapshot.soft_flags?.pdf_export_upgrade_recommended ? (
          <p className="text-sm text-[#6c6258]">
            Legacy Book preview is included on the free plan. Full PDF export will become an upgrade moment in a later
            release — you can still export today.
          </p>
        ) : null}
        <Button asChild variant="outline" size="sm" className="rounded-full border-[#c9a961]/40">
          <Link to="/pricing">View pricing</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
